import { RefObject, useEffect, useRef } from 'react';

export interface UseFocusTrapOptions {
  /** Whether the trap is currently engaged (e.g. the dialog is open). */
  active: boolean;
  /** Element to focus on activation instead of the first tabbable element. */
  initialFocusRef?: RefObject<HTMLElement>;
  /** Element to restore focus to on deactivation instead of whatever was focused before activation. */
  returnFocusTo?: RefObject<HTMLElement>;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Whether `element` (or an ancestor up to and including `container`) is hidden. */
function isHiddenWithin(element: HTMLElement, container: HTMLElement): boolean {
  let node: HTMLElement | null = element;
  while (node) {
    if (node.hidden || node.getAttribute('aria-hidden') === 'true') {
      return true;
    }
    if (node === container) {
      return false;
    }
    node = node.parentElement;
  }
  return false;
}

// Note: jsdom has no layout engine, so `offsetParent`/visibility checks
// (the usual way to filter out hidden elements) do not work in tests —
// this relies only on the selector above (disabled/tabindex/type attributes)
// plus an explicit walk for `[hidden]`/`[aria-hidden="true"]` ancestors,
// since neither of those is expressible as a single CSS selector.
function getTabbable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    element => !isHiddenWithin(element, container)
  );
}

/**
 * Traps Tab/Shift+Tab focus inside `containerRef` while `active` is true:
 * focuses `initialFocusRef` (or the first tabbable element) on activation,
 * wraps Tab from the last tabbable element back to the first (and vice
 * versa for Shift+Tab), and restores focus to `returnFocusTo` (or whatever
 * was focused immediately before activation) on deactivation/unmount. When
 * the container has no tabbable element at all (and no `initialFocusRef`),
 * the container itself is made focusable (a `tabindex="-1"` it did not
 * already have, removed again on cleanup) and focused, so activation never
 * silently leaves focus on whatever was behind it.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  { active, initialFocusRef, returnFocusTo }: UseFocusTrapOptions
) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    const tabbable = container ? getTabbable(container) : [];
    const toFocus = initialFocusRef?.current ?? tabbable[0];

    let addedTabIndex = false;
    if (toFocus) {
      toFocus.focus();
    } else if (container) {
      if (!container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
        addedTabIndex = true;
      }
      container.focus();
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab' || !container) {
        return;
      }

      const currentTabbable = getTabbable(container);
      if (currentTabbable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = currentTabbable[0];
      const last = currentTabbable[currentTabbable.length - 1];
      const current = document.activeElement;

      if (event.shiftKey) {
        if (current === first || !container.contains(current)) {
          event.preventDefault();
          last.focus();
        }
      } else if (current === last || !container.contains(current)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (addedTabIndex) {
        container?.removeAttribute('tabindex');
      }
      // Reading `.current` here (at deactivation time) rather than capturing
      // it up front is intentional — the caller's `returnFocusTo` target may
      // only exist once it renders, which can happen after this effect runs.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const returnTarget = returnFocusTo?.current ?? previouslyFocused.current;
      returnTarget?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

export default useFocusTrap;
